import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { GlassNav } from '../../components/layout/GlassNav';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Card, CardDescription, CardHeader, CardTitle } from '../../components/ui/Card';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import { register } from '../../services/auth';
import { api } from '../../services/api';
import { GOOGLE_OAUTH_URL } from '../../lib/constants';
import { isValidEmail, minLength } from '../../lib/validation';
import { getErrorMessage, getFieldErrors } from '../../lib/errorUtils';

export function RegisterPage() {
  const { loginSuccess } = useAuth();
  const { showToast } = useToast();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});
    
    if (!firstName.trim()) {
      setError('First name is required.');
      return;
    }
    if (firstName.trim().length > 100) {
      setFieldErrors({ firstName: 'First name must not exceed 100 characters.' });
      setError('Please fix the errors below.');
      return;
    }
    if (!lastName.trim()) {
      setError('Last name is required.');
      return;
    }
    if (lastName.trim().length > 100) {
      setFieldErrors({ lastName: 'Last name must not exceed 100 characters.' });
      setError('Please fix the errors below.');
      return;
    }
    if (!isValidEmail(email)) {
      setError('Enter a valid email address.');
      return;
    }
    if (phone.trim() && phone.trim().length > 20) {
      setFieldErrors({ phone: 'Phone must not exceed 20 characters.' });
      setError('Please fix the errors below.');
      return;
    }
    if (!minLength(password, 8)) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password.length > 100) {
      setFieldErrors({ password: 'Password must not exceed 100 characters.' });
      setError('Please fix the errors below.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    
    setLoading(true);
    try {
      const auth = await register({
        email: email.trim(),
        password,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone.trim() || undefined,
      });
      
      // Check if user is admin (auto-verified) or needs email verification
      if (auth.user.role === 'ADMIN') {
        loginSuccess(auth);
      } else {
        // Show verification message for non-admin users
        setRegisteredEmail(email.trim());
      }
    } catch (err) {
      const errors = getFieldErrors(err);
      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        setError('Please fix the errors below.');
      } else {
        const message = getErrorMessage(err, 'Unable to register. Try again.');
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const resendVerification = async () => {
    if (!registeredEmail) return;
    
    try {
      await api.post(`/auth/resend-verification?email=${encodeURIComponent(registeredEmail)}`, {}, { skipAuth: true } as any);
      showToast('Verification email has been sent. Please check your inbox.', 'success');
    } catch (err) {
      showToast(getErrorMessage(err, 'Unable to resend verification email.'), 'error');
    }
  };

  return (
    <div className="min-h-screen w-full bg-surface text-on-surface">
      <GlassNav />
      <div className="mx-auto flex max-w-lg flex-col gap-8 px-6 py-16">
        <Card>
          <CardHeader>
            <CardTitle>Create your account</CardTitle>
            <CardDescription>Sign up to access your secure client portal and manage your tax filings.</CardDescription>
          </CardHeader>
          
          {registeredEmail ? (
            <div className="space-y-6">
              <div className="rounded-lg bg-tertiary/20 p-6">
                <div className="flex items-start gap-3">
                  <span className="material-symbols-outlined text-3xl text-primary">mark_email_read</span>
                  <div>
                    <h3 className="text-lg font-bold text-primary">Check your email!</h3>
                    <p className="mt-2 text-sm text-on-surface/70">
                      We've sent a verification link to <strong>{registeredEmail}</strong>
                    </p>
                    <p className="mt-2 text-sm text-on-surface/70">
                      Click the link in the email to verify your account and complete registration.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="rounded-lg bg-surface-container p-4">
                  <p className="text-sm font-semibold text-on-surface">Didn't receive the email?</p>
                  <ul className="mt-2 space-y-1 text-xs text-on-surface/70">
                    <li>• Check your spam or junk folder</li>
                    <li>• Make sure you entered the correct email address</li>
                    <li>• Wait a few minutes for the email to arrive</li>
                  </ul>
                  <Button
                    onClick={resendVerification}
                    variant="secondary"
                    className="mt-4 w-full"
                  >
                    Resend Verification Email
                  </Button>
                </div>
                
                <div className="text-center text-sm">
                  <Link className="font-bold text-secondary hover:underline" to="/login">
                    Back to Sign in
                  </Link>
                </div>
              </div>
            </div>
          ) : (
            <>
              <form className="grid grid-cols-1 gap-6 md:grid-cols-2" onSubmit={submit}>
            <div>
              <Input
                label="First name"
                name="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
              />
              {fieldErrors.firstName && (
                <p className="mt-1 text-xs text-error">{fieldErrors.firstName}</p>
              )}
            </div>
            <div>
              <Input
                label="Last name"
                name="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
              />
              {fieldErrors.lastName && (
                <p className="mt-1 text-xs text-error">{fieldErrors.lastName}</p>
              )}
            </div>
            <div className="md:col-span-2">
              <Input
                label="Email"
                name="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              {fieldErrors.email && (
                <p className="mt-1 text-xs text-error">{fieldErrors.email}</p>
              )}
            </div>
            <div className="md:col-span-2">
              <Input
                label="Phone (optional)"
                name="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Max 20 characters"
              />
              {fieldErrors.phone && (
                <p className="mt-1 text-xs text-error">{fieldErrors.phone}</p>
              )}
            </div>
            <div className="md:col-span-2">
              <div className="relative">
                <Input
                  label="Password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="absolute right-4 top-11 text-sm font-semibold text-primary hover:text-secondary"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
              {fieldErrors.password && (
                <p className="mt-1 text-xs text-error">{fieldErrors.password}</p>
              )}
            </div>
            <div className="md:col-span-2">
              <div className="relative">
                <Input
                  label="Confirm Password"
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="absolute right-4 top-11 text-sm font-semibold text-primary hover:text-secondary"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? "Hide" : "Show"}
                </button>
              </div>
              {fieldErrors.confirmPassword && (
                <p className="mt-1 text-xs text-error">{fieldErrors.confirmPassword}</p>
              )}
            </div>
            {error ? (
              <p className="md:col-span-2 text-sm font-semibold text-error">{error}</p>
            ) : null}
            <div className="md:col-span-2">
              <Button type="submit" className="w-full" loading={loading}>
                Create account
              </Button>
            </div>
          </form>
          <div className="mt-6 space-y-4 text-center text-sm">
            <a className="block w-full" href={GOOGLE_OAUTH_URL}>
              <Button variant="secondary" className="w-full" type="button">
                Continue with Google
              </Button>
            </a>
            <p className="text-on-surface/70">
              Already registered?{' '}
              <Link className="font-bold text-secondary hover:underline" to="/login">
                Sign in
              </Link>
            </p>
          </div>
          </>
          )}
        </Card>
      </div>
    </div>
  );
}
